import React, { useEffect, useState } from 'react';
import { settingsApi } from '../services/api';
import toast from 'react-hot-toast';

const EMPTY_ABOUT = { title: '', paragraphs: '', closingStatement: '', whatWeDoTitle: '', whatWeDoItems: '' };
const EMPTY_CONTACT = {
  headline: '', subheadline: '', email: '', phone: '', whatsappNumber: '', whatsappMessage: '', address: '',
  facebookUrl: '', instagramUrl: '', linkedinUrl: '',
};
const EMPTY_BRANDS_PAGE = { introText: '', outroText: '' };
const EMPTY_GENERAL = { announcements: '' };

const TABS = [
  { key: 'about',      label: 'About Us' },
  { key: 'contact',    label: 'Contact Us' },
  { key: 'brandsPage', label: 'Our Brands (page text)' },
  { key: 'general',    label: 'Top Bar Announcements' },
];

function useSection(key, empty) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsApi.get(key).then((res) => setData({ ...empty, ...res.data })).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const save = async () => {
    setSaving(true);
    try {
      await settingsApi.save(key, data);
      toast.success('Saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  return { data, setData, save, saving };
}

function Field({ label, hint, children }) {
  return (
    <div className="form-field">
      <label>{label}</label>
      {children}
      {hint && <p className="meta-text" style={{ marginTop: 4 }}>{hint}</p>}
    </div>
  );
}

function AboutForm() {
  const { data, setData, save, saving } = useSection('about', EMPTY_ABOUT);
  if (!data) return <div className="spinner" />;
  const set = (field) => (e) => setData({ ...data, [field]: e.target.value });

  return (
    <div className="admin-section">
      <Field label="Page Title">
        <input value={data.title} onChange={set('title')} placeholder="About Swift & Sure Exim Pvt. Ltd." />
      </Field>
      <Field label="Body Paragraphs" hint="Separate paragraphs with a blank line.">
        <textarea value={data.paragraphs} onChange={set('paragraphs')} rows={12} />
      </Field>
      <Field label="Closing Statement (shown in bold)">
        <textarea value={data.closingStatement} onChange={set('closingStatement')} rows={2} />
      </Field>
      <Field label='"What We Do" Section Title'>
        <input value={data.whatWeDoTitle} onChange={set('whatWeDoTitle')} placeholder="What We Do" />
      </Field>
      <Field label='"What We Do" Bullet Points' hint="One item per line.">
        <textarea value={data.whatWeDoItems} onChange={set('whatWeDoItems')} rows={5} />
      </Field>
      <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save About Page'}</button>
    </div>
  );
}

function ContactForm() {
  const { data, setData, save, saving } = useSection('contact', EMPTY_CONTACT);
  if (!data) return <div className="spinner" />;
  const set = (field) => (e) => setData({ ...data, [field]: e.target.value });

  return (
    <div className="admin-section">
      <Field label="Headline">
        <input value={data.headline} onChange={set('headline')} placeholder="Get in Touch" />
      </Field>
      <Field label="Subheadline">
        <input value={data.subheadline} onChange={set('subheadline')} placeholder="Have a question or want to place a bulk order?" />
      </Field>
      <div className="form-grid">
        <Field label="Email">
          <input value={data.email} onChange={set('email')} placeholder="info@example.com" />
        </Field>
        <Field label="Phone (displayed)">
          <input value={data.phone} onChange={set('phone')} placeholder="+91 98765 43210" />
        </Field>
        <Field label="WhatsApp Number" hint="Digits only with country code, e.g. 919876543210 — used for the wa.me link.">
          <input value={data.whatsappNumber} onChange={set('whatsappNumber')} placeholder="919876543210" />
        </Field>
        <Field label="Address / Location">
          <input value={data.address} onChange={set('address')} placeholder="India" />
        </Field>
      </div>
      <Field label="Default WhatsApp Message">
        <textarea value={data.whatsappMessage} onChange={set('whatsappMessage')} rows={2} />
      </Field>
      <div className="form-grid">
        <Field label="Facebook URL" hint="Leave blank to hide the icon.">
          <input value={data.facebookUrl} onChange={set('facebookUrl')} placeholder="https://facebook.com/yourpage" />
        </Field>
        <Field label="Instagram URL" hint="Leave blank to hide the icon.">
          <input value={data.instagramUrl} onChange={set('instagramUrl')} placeholder="https://instagram.com/yourpage" />
        </Field>
        <Field label="LinkedIn URL" hint="Leave blank to hide the icon.">
          <input value={data.linkedinUrl} onChange={set('linkedinUrl')} placeholder="https://linkedin.com/company/yourpage" />
        </Field>
      </div>
      <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Contact Page'}</button>
    </div>
  );
}

function BrandsPageForm() {
  const { data, setData, save, saving } = useSection('brandsPage', EMPTY_BRANDS_PAGE);
  if (!data) return <div className="spinner" />;
  const set = (field) => (e) => setData({ ...data, [field]: e.target.value });

  return (
    <div className="admin-section">
      <Field label="Intro Text" hint="Shown above the brand cards. Manage the brand cards themselves on the Brands page.">
        <textarea value={data.introText} onChange={set('introText')} rows={3} />
      </Field>
      <Field label="Outro Text" hint="Shown below the brand cards, before the 'Partner With Us' button.">
        <textarea value={data.outroText} onChange={set('outroText')} rows={2} />
      </Field>
      <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Brands Page Text'}</button>
    </div>
  );
}

function GeneralForm() {
  const { data, setData, save, saving } = useSection('general', EMPTY_GENERAL);
  if (!data) return <div className="spinner" />;
  const set = (field) => (e) => setData({ ...data, [field]: e.target.value });

  return (
    <div className="admin-section">
      <Field label="Rotating Announcements" hint="Shown in the top bar above the navbar, one at a time, rotating every few seconds. One message per line.">
        <textarea value={data.announcements} onChange={set('announcements')} rows={5} />
      </Field>
      <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Announcements'}</button>
    </div>
  );
}

export default function SiteContent() {
  const [tab, setTab] = useState('about');

  return (
    <div className="admin-page">
      <h1 className="admin-page-title">Site Content</h1>
      <p className="meta-text" style={{ marginBottom: 16 }}>
        Edit the copy shown on the storefront's About, Contact, and Our Brands pages.
      </p>

      <div className="admin-tabs" style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'about' && <AboutForm />}
      {tab === 'contact' && <ContactForm />}
      {tab === 'brandsPage' && <BrandsPageForm />}
      {tab === 'general' && <GeneralForm />}
    </div>
  );
}
